import { useState } from "react";
import { lotteryGuessGames, companyAds, communityGuesses } from "@/mocks/news";

const colorMap: Record<string, { bg: string; text: string; border: string; badge: string }> = {
  amber: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", badge: "bg-amber-100 text-amber-700" },
  green: { bg: "bg-green-50", text: "text-green-700", border: "border-green-200", badge: "bg-green-100 text-green-700" },
  red: { bg: "bg-red-50", text: "text-red-700", border: "border-red-200", badge: "bg-red-100 text-red-700" },
  teal: { bg: "bg-teal-50", text: "text-teal-700", border: "border-teal-200", badge: "bg-teal-100 text-teal-700" },
  sky: { bg: "bg-sky-50", text: "text-sky-700", border: "border-sky-200", badge: "bg-sky-100 text-sky-700" },
  orange: { bg: "bg-orange-50", text: "text-orange-700", border: "border-orange-200", badge: "bg-orange-100 text-orange-700" },
  indigo: { bg: "bg-indigo-50", text: "text-indigo-700", border: "border-indigo-200", badge: "bg-indigo-100 text-indigo-700" },
  rose: { bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-200", badge: "bg-rose-100 text-rose-700" },
  slate: { bg: "bg-slate-50", text: "text-slate-700", border: "border-slate-200", badge: "bg-slate-100 text-slate-700" },
};

function DonationBanner() {
  const [donated, setDonated] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [amount, setAmount] = useState("20");

  const handleDonate = () => {
    setShowModal(false);
    setDonated(true);
    setTimeout(() => setDonated(false), 4000);
  };

  return (
    <>
      <div className="relative overflow-hidden rounded-2xl mb-8">
        <img
          src="https://public.readdy.ai/ai/img_res/bd6c1748519b83a70bf460698186f1a5.jpg"
          alt="Infants who need love and care"
          className="w-full h-56 md:h-64 object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        <div className="absolute inset-0 flex flex-col justify-center px-6 md:px-10">
          <div className="max-w-xl">
            <span className="inline-flex items-center gap-1.5 bg-rose-500 text-white text-xs font-bold px-3 py-1 rounded-full mb-3">
              <i className="ri-heart-fill"></i> Charity Mission
            </span>
            <h3 className="text-white text-xl md:text-2xl font-bold leading-tight mb-2">
              If You Win — Help an Infant in Need
            </h3>
            <p className="text-white/80 text-sm mb-4 max-w-md">
              Every infant deserves love, milk, clothes, and warmth. If your lottery guess wins, we invite you to make a donation for infants around the world who need our help.
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setShowModal(true)}
                className="px-5 py-2.5 bg-rose-500 hover:bg-rose-400 text-white font-bold rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-heart-line"></i> Donate for Infants
              </button>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2">
                <i className="ri-group-line text-white/70 text-sm"></i>
                <span className="text-white text-xs font-semibold">2,847 donors worldwide</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Donation stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { icon: "ri-heart-fill", color: "text-rose-500", bg: "bg-rose-50", label: "Total Donated", value: "$184,320" },
          { icon: "ri-user-smile-line", color: "text-amber-500", bg: "bg-amber-50", label: "Infants Helped", value: "3,210+" },
          { icon: "ri-global-line", color: "text-teal-500", bg: "bg-teal-50", label: "Countries Reached", value: "47" },
          { icon: "ri-hand-heart-line", color: "text-rose-400", bg: "bg-rose-50", label: "Donors This Month", value: "312" },
        ].map((stat) => (
          <div key={stat.label} className={`${stat.bg} rounded-xl p-4 text-center`}>
            <div className={`w-9 h-9 flex items-center justify-center mx-auto mb-2`}>
              <i className={`${stat.icon} ${stat.color} text-xl`}></i>
            </div>
            <p className="font-bold text-gray-900 text-lg">{stat.value}</p>
            <p className="text-stone-500 text-xs">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Donation success toast */}
      {donated && (
        <div className="fixed bottom-6 right-6 z-50 bg-rose-500 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-3 animate-bounce">
          <i className="ri-heart-fill text-lg"></i>
          <span className="font-semibold text-sm">Thank you! Your donation will change a life.</span>
        </div>
      )}

      {/* Donation modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <div className="text-center mb-5">
              <div className="w-14 h-14 flex items-center justify-center bg-rose-100 rounded-full mx-auto mb-3">
                <i className="ri-heart-fill text-rose-500 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900">Donate for Infants</h3>
              <p className="text-stone-500 text-sm mt-1">Your donation provides milk, clothes, and love to infants in need around the world.</p>
            </div>

            <div className="mb-4">
              <p className="text-sm font-semibold text-gray-700 mb-2">Select Amount</p>
              <div className="grid grid-cols-4 gap-2 mb-3">
                {["10", "20", "50", "100"].map((a) => (
                  <button
                    key={a}
                    onClick={() => setAmount(a)}
                    className={`py-2 rounded-lg text-sm font-bold border-2 transition-colors cursor-pointer ${
                      amount === a ? "border-rose-500 bg-rose-50 text-rose-600" : "border-stone-200 text-stone-600 hover:border-rose-300"
                    }`}
                  >
                    ${a}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 border-2 border-stone-200 rounded-lg px-3 py-2 focus-within:border-rose-400">
                <span className="text-stone-500 text-sm font-semibold">$</span>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="flex-1 outline-none text-sm text-gray-900"
                  placeholder="Custom amount"
                  min="1"
                />
              </div>
            </div>

            <div className="bg-rose-50 rounded-xl p-4 mb-5">
              <p className="text-rose-700 text-xs font-medium flex items-start gap-2">
                <i className="ri-information-line mt-0.5 flex-shrink-0"></i>
                100% of your donation goes directly to infant care programs — milk, clothing, medical care, and shelter for newborns in need worldwide.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 py-2.5 border border-stone-200 text-stone-600 font-semibold rounded-lg text-sm hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleDonate}
                className="flex-1 py-2.5 bg-rose-500 hover:bg-rose-400 text-white font-bold rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-heart-fill"></i> Donate ${amount}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function GuessPostModal({ onClose, onSubmit }: { onClose: () => void; onSubmit: (data: { name: string; numbers: number[]; game: string; comment: string }) => void }) {
  const [name, setName] = useState("");
  const [selectedGame, setSelectedGame] = useState(0);
  const [guessNumbers, setGuessNumbers] = useState<number[]>([]);
  const [comment, setComment] = useState("");
  const [step, setStep] = useState<"form" | "success">("form");

  const toggleNumber = (n: number) => {
    setGuessNumbers((prev) =>
      prev.includes(n) ? prev.filter((x) => x !== n) : prev.length < 5 ? [...prev, n] : prev
    );
  };

  const handleSubmit = () => {
    if (!name.trim() || guessNumbers.length < 5) return;
    onSubmit({ name, numbers: guessNumbers, game: lotteryGuessGames[selectedGame].name, comment });
    setStep("success");
  };

  if (step === "success") {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
        <div className="bg-white rounded-2xl p-8 w-full max-w-md text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-amber-100 rounded-full mx-auto mb-4">
            <i className="ri-checkbox-circle-fill text-amber-500 text-3xl"></i>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Your Guess is Posted!</h3>
          <p className="text-stone-500 text-sm mb-2">Your numbers are now visible to the community.</p>
          <div className="flex justify-center gap-2 my-4">
            {guessNumbers.map((n) => (
              <div key={n} className="w-10 h-10 flex items-center justify-center rounded-full bg-amber-500 font-bold text-white text-sm">
                {n}
              </div>
            ))}
          </div>
          <p className="text-rose-500 text-sm font-semibold mb-5">
            <i className="ri-heart-fill mr-1"></i>
            If you win — please consider donating for infants in need!
          </p>
          <button onClick={onClose} className="px-6 py-2.5 bg-stone-900 text-white font-semibold rounded-lg text-sm cursor-pointer whitespace-nowrap">
            Back to Community Board
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 overflow-y-auto py-6">
      <div className="bg-white rounded-2xl p-6 w-full max-w-lg my-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-gray-900">Post Your Lottery Guess</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-stone-100 cursor-pointer">
            <i className="ri-close-line text-stone-500"></i>
          </button>
        </div>

        <div className="mb-4">
          <label className="text-sm font-semibold text-gray-700 block mb-1.5">Your Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter your name or nickname"
            className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-amber-400"
          />
        </div>

        <div className="mb-4">
          <label className="text-sm font-semibold text-gray-700 block mb-1.5">Select Lottery Game</label>
          <div className="flex gap-2 flex-wrap">
            {lotteryGuessGames.map((g, i) => (
              <button
                key={g.id}
                onClick={() => setSelectedGame(i)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  selectedGame === i ? "bg-amber-500 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                }`}
              >
                {g.name} — {g.jackpot}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <label className="text-sm font-semibold text-gray-700 block mb-1.5">
            Pick 5 Numbers <span className="text-stone-400 font-normal">({guessNumbers.length}/5 selected)</span>
          </label>
          <div className="grid grid-cols-10 gap-1 mb-3">
            {Array.from({ length: 60 }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                onClick={() => toggleNumber(n)}
                className={`w-full aspect-square flex items-center justify-center rounded text-xs font-semibold transition-colors cursor-pointer ${
                  guessNumbers.includes(n)
                    ? "bg-amber-500 text-white"
                    : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                }`}
              >
                {n}
              </button>
            ))}
          </div>
          <div className="flex gap-2 justify-center">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className={`w-10 h-10 flex items-center justify-center rounded-full border-2 font-bold text-sm ${
                  guessNumbers[i]
                    ? "border-amber-500 bg-amber-500 text-white"
                    : "border-stone-200 text-stone-300"
                }`}
              >
                {guessNumbers[i] || "?"}
              </div>
            ))}
          </div>
        </div>

        <div className="mb-5">
          <label className="text-sm font-semibold text-gray-700 block mb-1.5">Message to Community <span className="text-stone-400 font-normal">(optional)</span></label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, 200))}
            placeholder="Share your thoughts — why these numbers? Will you donate if you win?"
            className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-amber-400 resize-none h-20"
            maxLength={200}
          />
          <p className="text-xs text-stone-400 text-right">{comment.length}/200</p>
        </div>

        <div className="bg-rose-50 rounded-xl p-3 mb-5 flex items-start gap-2">
          <i className="ri-heart-fill text-rose-400 mt-0.5 flex-shrink-0"></i>
          <p className="text-rose-600 text-xs">By posting your guess, you join our community pledge: <strong>if you win, consider donating for infants in need around the world.</strong></p>
        </div>

        <button
          onClick={handleSubmit}
          disabled={!name.trim() || guessNumbers.length < 5}
          className="w-full py-3 bg-amber-500 hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap"
        >
          Post My Guess to Community Board
        </button>
      </div>
    </div>
  );
}

function CommunityGuessBoard() {
  const [likedIds, setLikedIds] = useState<number[]>([]);
  const [showPostModal, setShowPostModal] = useState(false);
  const [localGuesses, setLocalGuesses] = useState(communityGuesses);
  const [filterGame, setFilterGame] = useState("All");
  const [showDonateModal, setShowDonateModal] = useState(false);
  const [donateAmount, setDonateAmount] = useState("20");
  const [donated, setDonated] = useState(false);

  const toggleLike = (id: number) => {
    setLikedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleNewGuess = (data: { name: string; numbers: number[]; game: string; comment: string }) => {
    const newEntry = {
      id: Date.now(),
      user: data.name,
      avatar: data.name.slice(0, 2).toUpperCase(),
      country: "🌍",
      numbers: data.numbers,
      game: data.game,
      date: "Just now",
      likes: 0,
      comment: data.comment || "Just posted my lucky numbers!",
    };
    setLocalGuesses((prev) => [newEntry, ...prev]);
  };

  const handleDonate = () => {
    setShowDonateModal(false);
    setDonated(true);
    setTimeout(() => setDonated(false), 4000);
  };

  const games = ["All", ...lotteryGuessGames.map((g) => g.name)];
  const filtered = filterGame === "All" ? localGuesses : localGuesses.filter((g) => g.game === filterGame);

  return (
    <div>
      {/* Mission statement */}
      <div className="bg-gradient-to-r from-rose-50 to-amber-50 border border-rose-100 rounded-2xl p-5 mb-6 flex flex-col md:flex-row items-center gap-4">
        <div className="w-14 h-14 flex items-center justify-center bg-rose-100 rounded-full flex-shrink-0">
          <i className="ri-heart-2-line text-rose-500 text-2xl"></i>
        </div>
        <div className="flex-1 text-center md:text-left">
          <h3 className="font-bold text-gray-900 text-base mb-1">Guess Together, Give Together</h3>
          <p className="text-stone-600 text-sm">
            Post your lottery guess numbers publicly. If anyone in our community wins using these numbers, we encourage them to <strong className="text-rose-600">make a donation for infants</strong> around the world who need love, milk, clothes, and care.
          </p>
        </div>
        <button
          onClick={() => setShowDonateModal(true)}
          className="px-5 py-2.5 bg-rose-500 hover:bg-rose-400 text-white font-bold rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 flex-shrink-0"
        >
          <i className="ri-heart-fill"></i> Donate Now
        </button>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5">
        <div className="flex flex-wrap gap-2">
          {games.map((g) => (
            <button
              key={g}
              onClick={() => setFilterGame(g)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                filterGame === g ? "bg-stone-900 text-white" : "bg-white border border-stone-200 text-stone-600 hover:border-stone-400"
              }`}
            >
              {g}
            </button>
          ))}
        </div>
        <button
          onClick={() => setShowPostModal(true)}
          className="px-5 py-2 bg-amber-500 hover:bg-amber-400 text-white font-bold rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
        >
          <i className="ri-add-line"></i> Post Your Guess
        </button>
      </div>

      {/* Guess cards grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
        {filtered.map((guess) => (
          <div key={guess.id} className="bg-white border border-stone-100 rounded-xl p-4 hover:border-amber-200 transition-colors">
            {/* User header */}
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-amber-100 font-bold text-amber-700 text-sm flex-shrink-0">
                {guess.avatar}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="font-semibold text-gray-900 text-sm truncate">{guess.user}</span>
                  <span className="text-base">{guess.country}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-stone-400">{guess.date}</span>
                  <span className="text-xs bg-stone-100 text-stone-500 px-2 py-0.5 rounded-full">{guess.game}</span>
                </div>
              </div>
            </div>

            {/* Numbers */}
            <div className="flex gap-1.5 mb-3 flex-wrap">
              {guess.numbers.map((n) => (
                <div
                  key={n}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-white font-bold text-sm"
                >
                  {n}
                </div>
              ))}
            </div>

            {/* Comment */}
            {guess.comment && (
              <p className="text-stone-600 text-xs mb-3 line-clamp-2 italic">&ldquo;{guess.comment}&rdquo;</p>
            )}

            {/* Actions */}
            <div className="flex items-center justify-between pt-2 border-t border-stone-50">
              <button
                onClick={() => toggleLike(guess.id)}
                className={`flex items-center gap-1.5 text-xs font-semibold transition-colors cursor-pointer ${
                  likedIds.includes(guess.id) ? "text-rose-500" : "text-stone-400 hover:text-rose-400"
                }`}
              >
                <i className={likedIds.includes(guess.id) ? "ri-heart-fill" : "ri-heart-line"}></i>
                {guess.likes + (likedIds.includes(guess.id) ? 1 : 0)}
              </button>
              <button
                onClick={() => setShowDonateModal(true)}
                className="flex items-center gap-1 text-xs text-rose-500 hover:text-rose-600 font-semibold cursor-pointer transition-colors"
              >
                <i className="ri-hand-heart-line"></i> Donate for Infants
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Bottom CTA */}
      <div className="bg-gradient-to-br from-stone-900 to-stone-800 rounded-2xl p-6 text-white text-center">
        <i className="ri-trophy-line text-3xl text-amber-400 mb-3 block"></i>
        <h3 className="font-bold text-lg mb-1">Join the Community Guess Board</h3>
        <p className="text-white/70 text-sm mb-4 max-w-lg mx-auto">
          Sign up, post your lucky numbers, and be part of a community that cares. If you win — help an infant somewhere in the world get the love and care they deserve.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => setShowPostModal(true)}
            className="px-6 py-2.5 bg-amber-500 hover:bg-amber-400 text-white font-bold rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line mr-1"></i> Post My Guess Numbers
          </button>
          <button
            onClick={() => setShowDonateModal(true)}
            className="px-6 py-2.5 bg-rose-500 hover:bg-rose-400 text-white font-bold rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-heart-fill mr-1"></i> Donate for Infants
          </button>
        </div>
      </div>

      {/* Post modal */}
      {showPostModal && (
        <GuessPostModal
          onClose={() => setShowPostModal(false)}
          onSubmit={(data) => { handleNewGuess(data); }}
        />
      )}

      {/* Donate modal */}
      {showDonateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <div className="text-center mb-5">
              <div className="w-16 h-16 flex items-center justify-center bg-rose-100 rounded-full mx-auto mb-3">
                <i className="ri-heart-2-fill text-rose-500 text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900">Help an Infant Today</h3>
              <p className="text-stone-500 text-sm mt-1">Your donation provides milk, warm clothes, and love to infants in need around the world.</p>
            </div>

            <div className="mb-4">
              <p className="text-sm font-semibold text-gray-700 mb-2">Choose Donation Amount</p>
              <div className="grid grid-cols-4 gap-2 mb-3">
                {["10", "20", "50", "100"].map((a) => (
                  <button
                    key={a}
                    onClick={() => setDonateAmount(a)}
                    className={`py-2 rounded-lg text-sm font-bold border-2 transition-colors cursor-pointer ${
                      donateAmount === a ? "border-rose-500 bg-rose-50 text-rose-600" : "border-stone-200 text-stone-600 hover:border-rose-300"
                    }`}
                  >
                    ${a}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 border-2 border-stone-200 rounded-lg px-3 py-2 focus-within:border-rose-400">
                <span className="text-stone-500 text-sm font-semibold">$</span>
                <input
                  type="number"
                  value={donateAmount}
                  onChange={(e) => setDonateAmount(e.target.value)}
                  className="flex-1 outline-none text-sm text-gray-900"
                  placeholder="Custom amount"
                  min="1"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-5 text-center">
              {[
                { amount: "$10", impact: "1 week of milk for 1 infant" },
                { amount: "$20", impact: "Warm clothes set for 1 baby" },
                { amount: "$50", impact: "Monthly care for 1 infant" },
              ].map((item) => (
                <div key={item.amount} className="bg-rose-50 rounded-xl p-3">
                  <p className="font-bold text-rose-600 text-sm">{item.amount}</p>
                  <p className="text-rose-500 text-xs mt-1">{item.impact}</p>
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowDonateModal(false)}
                className="flex-1 py-2.5 border border-stone-200 text-stone-600 font-semibold rounded-lg text-sm hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleDonate}
                className="flex-1 py-2.5 bg-rose-500 hover:bg-rose-400 text-white font-bold rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-heart-fill"></i> Donate ${donateAmount}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Donation success toast */}
      {donated && (
        <div className="fixed bottom-6 right-6 z-50 bg-rose-500 text-white px-5 py-3 rounded-xl flex items-center gap-3">
          <i className="ri-heart-fill text-lg"></i>
          <span className="font-semibold text-sm">Thank you! Your donation will change a life.</span>
        </div>
      )}
    </div>
  );
}

function AdCard({ ad }: { ad: typeof companyAds[0] }) {
  const c = colorMap[ad.color] || colorMap.amber;
  return (
    <div className={`rounded-xl border ${c.border} overflow-hidden hover:scale-[1.01] transition-transform`}>
      <div className="w-full h-36 overflow-hidden">
        <img src={ad.image} alt={ad.company} className="w-full h-full object-cover object-top" />
      </div>
      <div className={`p-4 ${c.bg}`}>
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="font-bold text-gray-900 text-sm leading-tight">{ad.company}</h3>
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${
            ad.badge === "Gold Member" ? "bg-amber-100 text-amber-700" : "bg-stone-100 text-stone-600"
          }`}>
            {ad.badge === "Gold Member" ? "★ Gold" : "Basic"}
          </span>
        </div>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${c.badge} inline-block mb-2`}>
          <i className={`${ad.icon} mr-1`}></i>{ad.category}
        </span>
        <p className="text-xs text-stone-600 mb-2 line-clamp-2">{ad.description}</p>
        <div className="flex items-center gap-1 text-xs text-stone-500">
          <i className="ri-map-pin-line"></i>
          <span>{ad.location}</span>
        </div>
      </div>
    </div>
  );
}

export default function NewsLotterySection() {
  const [activeTab, setActiveTab] = useState<"lottery" | "ads">("lottery");
  const [adFilter, setAdFilter] = useState("All");

  const categories = ["All", "Restaurant", "CPA & Accounting", "CPA & Tax", "CPA & Legal", "Logistics", "Business Services"];
  const filtered = adFilter === "All" ? companyAds : companyAds.filter((a) => a.category === adFilter);

  return (
    <section id="lottery-ads" className="py-16 bg-stone-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-8">
          <span className="text-rose-500 text-sm font-semibold uppercase tracking-wider flex items-center justify-center gap-1.5 mb-1">
            <i className="ri-heart-fill"></i> Community &amp; Charity
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-1">Welcome Lottery Guess &amp; Business Ads</h2>
          <p className="text-stone-500 text-sm mt-2 max-w-xl mx-auto">
            Post your lucky numbers, share with the world — and if you win, help infants in need get the love, milk, and warmth they deserve.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-white border border-stone-200 rounded-full p-1">
            <button
              onClick={() => setActiveTab("lottery")}
              className={`px-6 py-2 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === "lottery" ? "bg-amber-500 text-white" : "text-stone-600 hover:text-stone-900"
              }`}
            >
              <i className="ri-trophy-line mr-1.5"></i>Lottery Guess &amp; Charity
            </button>
            <button
              onClick={() => setActiveTab("ads")}
              className={`px-6 py-2 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === "ads" ? "bg-amber-500 text-white" : "text-stone-600 hover:text-stone-900"
              }`}
            >
              <i className="ri-megaphone-line mr-1.5"></i>Company Ads
            </button>
          </div>
        </div>

        {/* Lottery + Charity Tab */}
        {activeTab === "lottery" && (
          <div>
            <DonationBanner />
            <CommunityGuessBoard />
          </div>
        )}

        {/* Company Ads Tab */}
        {activeTab === "ads" && (
          <div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 flex flex-col sm:flex-row items-center justify-between gap-3 mb-6">
              <div className="flex items-center gap-2">
                <i className="ri-megaphone-line text-amber-500 text-lg"></i>
                <span className="text-amber-800 text-sm font-medium">Restaurants, CPA firms, and all companies can post ads here. <strong>Basic $5/mo · Gold $18/mo</strong></span>
              </div>
              <a href="/membership" className="px-4 py-1.5 bg-amber-500 text-white text-xs font-semibold rounded-full hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap">
                Post Your Ad
              </a>
            </div>

            <div className="flex flex-wrap gap-2 mb-6">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setAdFilter(cat)}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    adFilter === cat
                      ? "bg-stone-900 text-white"
                      : "bg-white border border-stone-200 text-stone-600 hover:border-stone-400"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {filtered.map((ad) => (
                <AdCard key={ad.id} ad={ad} />
              ))}
            </div>

            <div className="mt-10 text-center bg-gradient-to-r from-stone-900 to-stone-800 rounded-2xl p-8 text-white">
              <i className="ri-store-2-line text-4xl text-amber-400 mb-3 block"></i>
              <h3 className="text-xl font-bold mb-2">Advertise Your Business Here</h3>
              <p className="text-white/70 text-sm mb-5 max-w-lg mx-auto">
                Reach thousands of buyers, importers, and business owners daily. Restaurants, CPA firms, logistics companies, and all businesses welcome!
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a href="/membership" className="px-6 py-2.5 bg-amber-500 hover:bg-amber-400 text-white font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm">
                  Basic — $5/month
                </a>
                <a href="/membership" className="px-6 py-2.5 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm border border-white/20">
                  Gold — $18/month
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
